// Canonical date rendering for the whole app. Every page that shows a date should
// import from here. See M13 #294 — the same date was rendering three different ways
// ("1 Sep 2026" / "01 Sep 2026" / "01 Sep") depending only on which page you were on.

function parse(dateStr: string): Date {
  // A date-only string ("2026-07-10") parses as UTC midnight, which can land on the
  // previous calendar day in a negative-UTC-offset timezone. Pin it to local midnight.
  return /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? new Date(dateStr + 'T00:00:00') : new Date(dateStr)
}

/** "1 Sep 2026" — no leading zero on the day; reads more naturally than "01 Sep 2026". */
export function formatDate(dateStr: string): string {
  return parse(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * "1 Sep" — deliberately omits the year. Only for lists that are always recent
 * (e.g. an account's transaction ledger), where the year would be noise on every row.
 * Not a general-purpose substitute for `formatDate`.
 */
export function formatShortDate(dateStr: string): string {
  return parse(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
  })
}

/** "September 2026", from a yyyy-MM month. */
export function formatMonth(month: string): string {
  return parse(`${month}-01`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
}

/** "Oct", from a yyyy-MM month. */
export function formatShortMonth(month: string): string {
  return parse(`${month}-01`).toLocaleDateString('en-IN', { month: 'short' })
}

/** "October", from a yyyy-MM month. */
export function formatMonthName(month: string): string {
  return parse(`${month}-01`).toLocaleDateString('en-IN', { month: 'long' })
}

/** "Dec 26" — a target date at a glance, where the day doesn't matter. */
export function formatMonthYearShort(dateStr: string): string {
  return parse(dateStr).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })
}
